import React, { useState, useRef, useCallback, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND || `http://${window.location.hostname}:3000`;
const PEER_OPTIONS = (() => {
  try {
    const url = new URL(BACKEND_URL);
    return { host: url.hostname, port: url.port || "80", path: "/peerjs" };
  } catch {
    return { host: window.location.hostname, port: window.location.port || "80", path: "/peerjs" };
  }
})();

export default function VideoChat({ meetingCode, currentUser, socket, users, onSwitchTab }) {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [remoteStreams, setRemoteStreams] = useState([]);

  const localStreamRef = useRef(null);
  const peerRef = useRef(null);
  const peersMapRef = useRef({});
  const remoteStreamsRef = useRef({});

  const peerId = `${meetingCode}-video-${currentUser}`;

  const updateStreams = useCallback(() => {
    setRemoteStreams(Object.values(remoteStreamsRef.current));
  }, []);

  const notifyJoined = useCallback(() => {
    if (socket) {
      socket.emit("video-joined", { meetingCode, user: currentUser });
    }
  }, [socket, meetingCode, currentUser]);

  const notifyLeft = useCallback(() => {
    if (socket) {
      socket.emit("video-left", { meetingCode, user: currentUser });
    }
  }, [socket, meetingCode, currentUser]);

  const cleanup = useCallback(() => {
    Object.values(peersMapRef.current).forEach((c) => {
      try { c.close(); } catch {}
    });
    peersMapRef.current = {};
    remoteStreamsRef.current = {};
    setRemoteStreams([]);
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
  }, []);

  const leaveCall = useCallback(() => {
    notifyLeft();
    cleanup();
    setStatus("idle");
    setError(null);
  }, [cleanup, notifyLeft]);

  const callUser = useCallback((userName) => {
    if (!localStreamRef.current || !peerRef.current) return;
    const targetId = `${meetingCode}-video-${userName}`;
    if (peersMapRef.current[targetId]) return;

    try {
      const call = peerRef.current.call(targetId, localStreamRef.current, {
        metadata: { name: currentUser },
      });

      call.on("stream", (remoteStream) => {
        if (!remoteStreamsRef.current[targetId]) {
          remoteStreamsRef.current[targetId] = {
            id: targetId,
            stream: remoteStream,
            name: userName,
          };
          updateStreams();
        }
      });

      call.on("close", () => {
        delete peersMapRef.current[targetId];
        delete remoteStreamsRef.current[targetId];
        updateStreams();
      });

      call.on("error", (err) => {
        console.warn(`callUser error for ${userName}:`, err);
        delete peersMapRef.current[targetId];
        delete remoteStreamsRef.current[targetId];
        updateStreams();
      });

      peersMapRef.current[targetId] = call;
    } catch (err) {
      console.warn(`Could not call ${userName}:`, err);
    }
  }, [meetingCode, currentUser, updateStreams]);

  const joinCall = useCallback(async () => {
    setError(null);
    setStatus("joining");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      localStreamRef.current = stream;

      const peer = new window.Peer(peerId, PEER_OPTIONS);
      peerRef.current = peer;

      peer.on("open", () => {
        setStatus("joined");
        notifyJoined();
      });

      peer.on("call", (call) => {
        const theirId = call.peer;
        if (peersMapRef.current[theirId]) {
          call.close();
          return;
        }
        const theirName = call.metadata?.name || theirId;
        call.answer(localStreamRef.current);

        call.on("stream", (remoteStream) => {
          if (!remoteStreamsRef.current[theirId]) {
            remoteStreamsRef.current[theirId] = {
              id: theirId,
              stream: remoteStream,
              name: theirName,
            };
            updateStreams();
          }
        });

        call.on("close", () => {
          delete peersMapRef.current[theirId];
          delete remoteStreamsRef.current[theirId];
          updateStreams();
        });

        peersMapRef.current[theirId] = call;
      });

      peer.on("error", (err) => {
        console.error("Peer error:", err);
        if (err.type === "unavailable-id") {
          setError(
            "Another session with your ID is active. Please refresh."
          );
        } else {
          setError("Connection error. Please try again.");
        }
        leaveCall();
      });
    } catch (err) {
      if (
        err.name === "NotAllowedError" ||
        err.name === "NotFoundError"
      ) {
        setError(
          "Camera/microphone access denied. Please allow permissions."
        );
      } else if (err.name === "NotReadableError") {
        setError(
          "Camera/microphone is busy. Close other apps using it."
        );
      } else {
        setError("Could not start video: " + err.message);
      }
      setStatus("idle");
    }
  }, [peerId, notifyJoined, leaveCall, updateStreams]);

  useEffect(() => {
    if (!socket || status !== "joined") return;

    const onVideoJoined = ({ user }) => {
      if (user === currentUser) return;
      setTimeout(() => callUser(user), 600);
    };

    const onVideoLeft = ({ user }) => {
      const targetId = `${meetingCode}-video-${user}`;
      if (peersMapRef.current[targetId]) {
        try {
          peersMapRef.current[targetId].close();
        } catch {}
        delete peersMapRef.current[targetId];
      }
      delete remoteStreamsRef.current[targetId];
      updateStreams();
    };

    socket.on("video-joined", onVideoJoined);
    socket.on("video-left", onVideoLeft);

    if (users && users.length > 0) {
      users.forEach((u) => {
        if (u.name !== currentUser) {
          setTimeout(() => callUser(u.name), 1000);
        }
      });
    }

    return () => {
      socket.off("video-joined", onVideoJoined);
      socket.off("video-left", onVideoLeft);
    };
  }, [socket, status, currentUser, meetingCode, callUser, updateStreams, users]);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const toggleAudio = () => {
    const enabled = !audioEnabled;
    localStreamRef.current?.getAudioTracks().forEach((t) => (t.enabled = enabled));
    setAudioEnabled(enabled);
  };

  const toggleVideo = () => {
    const enabled = !videoEnabled;
    localStreamRef.current?.getVideoTracks().forEach((t) => (t.enabled = enabled));
    setVideoEnabled(enabled);
  };

  if (currentUser === "NA") {
    return (
      <div className="video-section">
        <div className="video-hero">
          <div className="video-hero-icon">
            <i className="bi bi-plug"></i>
          </div>
          <h2 className="video-hero-title">Not Connected</h2>
          <p className="video-hero-desc">
            Go to the <strong>Code</strong> tab first to join the room. Once
            connected, return here to start a video call.
          </p>
          <div className="screen-controls">
            <button
              className="btn-share primary"
              onClick={() => onSwitchTab && onSwitchTab("code")}
            >
              <i className="bi bi-code-slash"></i>
              Go to Code Tab
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-section">
      <div className="video-hero">
        {status === "idle" && (
          <>
            <div className="video-hero-icon">
              <i className="bi bi-camera-video-fill"></i>
            </div>
            <h2 className="video-hero-title">Video Chat</h2>
            <p className="video-hero-desc">
              Start a video call with your collaborators.
            </p>
            <button
              className="btn-share success"
              onClick={joinCall}
            >
              <i className="bi bi-camera-video-fill"></i>
              Join Video Call
            </button>
          </>
        )}

        {status === "joining" && (
          <>
            <div className="video-hero-icon">
              <i className="bi bi-camera-video-fill"></i>
            </div>
            <h2 className="video-hero-title">Joining...</h2>
            <p className="video-hero-desc">
              Setting up camera and microphone.
            </p>
            <div className="screen-spinner"></div>
          </>
        )}

        {status === "error" && error && (
          <>
            <div className="screen-error">
              <i className="bi bi-exclamation-triangle"></i>
              {error}
            </div>
            <button
              className="btn-share primary"
              onClick={() => setStatus("idle")}
              style={{ marginTop: 12 }}
            >
              Try Again
            </button>
          </>
        )}
      </div>

      {status === "joined" && (
        <>
          <div className="video-grid">
            <div className="video-tile local-tile">
              <video
                ref={(el) => {
                  if (el && localStreamRef.current) {
                    el.srcObject = localStreamRef.current;
                  }
                }}
                muted
                playsInline
                autoPlay
              />
              <div className="video-tile-name">
                <i className="bi bi-person"></i>
                {currentUser}
              </div>
            </div>

            {remoteStreams.length === 0 && (
              <div className="video-tile video-tile-empty">
                <div className="video-empty-msg">
                  <i className="bi bi-people"></i>
                  <p>Waiting for others to join...</p>
                </div>
              </div>
            )}

            {remoteStreams.map(({ id, stream, name }) => (
              <div key={id} className="video-tile">
                <video
                  ref={(el) => {
                    if (el && el.srcObject !== stream) {
                      el.srcObject = stream;
                    }
                  }}
                  autoPlay
                  playsInline
                />
                <div className="video-tile-name">
                  <i className="bi bi-person"></i>
                  {name}
                </div>
              </div>
            ))}
          </div>

          <div className="video-controls">
            <button
              className={
                "video-ctrl-btn" + (audioEnabled ? "" : " muted")
              }
              onClick={toggleAudio}
              title={audioEnabled ? "Mute microphone" : "Unmute microphone"}
            >
              <i
                className={
                  "bi " + (audioEnabled ? "bi-mic" : "bi-mic-mute")
                }
              ></i>
            </button>
            <button
              className={
                "video-ctrl-btn" + (videoEnabled ? "" : " muted")
              }
              onClick={toggleVideo}
              title={videoEnabled ? "Turn off camera" : "Turn on camera"}
            >
              <i
                className={
                  "bi " +
                  (videoEnabled
                    ? "bi-camera-video"
                    : "bi-camera-video-off")
                }
              ></i>
            </button>
            <button
              className="video-ctrl-btn leave"
              onClick={leaveCall}
              title="Leave video call"
            >
              <i className="bi bi-telephone-x-fill"></i>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
